import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { A as Accessibility, a as addAccessibility } from './accessibility-_zCLcLLO.js';
import { W as WheelGesturesPlugin } from './embla-carousel-wheel-gestures.esm-BxgUSOtT.js';
import { a as addPrevNextButtonClickHandlers } from './arrow-buttons-CGj8dylI.js';
import { a as addDots } from './dots-By_KY3Cn.js';

class ProductMedia extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  #embla;
  /** @type {IntersectionObserver | undefined} */
  #videoObserver;
  /** @type {Promise<void> | undefined} */
  #fancyboxLoading;
  /** @type {(() => void) | undefined} */
  #unbindFancybox;
  /** @type {AbortController | undefined} */
  #abortController;
  #fancyboxSelector = '[data-fancybox="product-gallery"]';

  connectedCallback() {
    this.#destroy();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initVideoControls(signal);
    this.#initNativeVideoListeners(signal);
    this.#initEmbla(signal);
    this.#initFancybox(signal);
    this.#initVideoObserver();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #destroy() {
    this.removeEventListener('click', this.#handleVideoClick, { capture: true });
    this.removeEventListener('play', this.#handleNativeVideoPlay, { capture: true });
    this.removeEventListener('ended', this.#handleNativeVideoEnded, { capture: true });

    const unbindFancybox = this.#unbindFancybox;
    this.#unbindFancybox = undefined;
    this.#fancyboxLoading = undefined;
    unbindFancybox?.();

    this.#abortController?.abort();
    this.#abortController = undefined;

    this.#videoObserver?.disconnect();
    this.#videoObserver = undefined;

    this.#embla?.destroy();
    this.#embla = undefined;
  }

  #handleVideoClick = (event) => {
    const playTrigger = event.target.closest('[data-media-video-play]');
    if (!playTrigger) return;

    event.preventDefault();
    event.stopPropagation();

    const videoSlide = playTrigger.closest('[data-item-video]');
    if (videoSlide) this.#playVideo(videoSlide);
  };

  #handleNativeVideoPlay = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;

    const container = event.target.closest('[data-item-video]');
    if (!container) return;

    container.dataset.playing = 'true';
  };

  #handleNativeVideoEnded = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;

    const container = event.target.closest('[data-item-video]');
    if (!container) return;

    // Restore the poster only when playback finishes — not on pause, so native
    // controls (pause / seek / volume) can stay usable on the inline player.
    container.dataset.playing = 'false';
  };

  /**
   * @param {AbortSignal} signal
   */
  #initVideoControls(signal) {
    this.addEventListener('click', this.#handleVideoClick, { capture: true, signal });
  }

  /**
   * @param {AbortSignal} signal
   */
  #initNativeVideoListeners(signal) {
    this.addEventListener('play', this.#handleNativeVideoPlay, { capture: true, signal });
    this.addEventListener('ended', this.#handleNativeVideoEnded, { capture: true, signal });
  }

  /**
   * @param {HTMLElement} container
   */
  #playVideo(container) {
    if (container.dataset.playing === 'true') return;

    const player = container.querySelector('[data-media-video-player]');
    if (!player) return;

    // Show the player before play(). `hidden` is display:none, and Chrome
    // rejects play() on a video that is not rendered.
    container.dataset.playing = 'true';
    this.#startPlayer(container.dataset.videoType, player, container);
  }

  /**
   * @param {string | undefined} videoType
   * @param {Element | null | undefined} player
   * @param {HTMLElement} [container]
   */
  #startPlayer(videoType, player, container) {
    if (!player) return;

    if (videoType === 'native') {
      const video = player.querySelector('video');
      if (!video) return;

      void player.offsetWidth;
      video.play()?.catch((error) => {
        if (error.name === 'AbortError') return;
        if (container?.isConnected) container.dataset.playing = 'false';
      });
      return;
    }

    const iframe = player.querySelector('iframe');
    if (iframe?.dataset.src && !iframe.src) {
      iframe.src = iframe.dataset.src;
    }
  }

  /**
   * @param {HTMLElement} container
   */
  #pauseVideo(container) {
    const player = container.querySelector('[data-media-video-player]');

    this.#pausePlayer(container.dataset.videoType, player);

    container.dataset.playing = 'false';
  }

  /**
   * @param {string | undefined} videoType
   * @param {Element | null | undefined} player
   */
  #pausePlayer(videoType, player) {
    if (!player) return;

    if (videoType === 'native') {
      player.querySelector('video')?.pause();
      return;
    }

    const iframe = player.querySelector('iframe');
    if (!iframe) return;

    if (iframe.contentWindow) {
      const message =
        videoType === 'vimeo'
          ? JSON.stringify({ method: 'pause' })
          : JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' });

      iframe.contentWindow.postMessage(message, '*');
    }

    iframe.removeAttribute('src');
  }

  #initVideoObserver() {
    const videoSlides = [...this.querySelectorAll('[data-item-video]')];
    if (!videoSlides.length) return;

    // Pause all gallery videos when the gallery scrolls off the page.
    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => !entry.isIntersecting)) {
          videoSlides.forEach((slide) => this.#pauseVideo(/** @type {HTMLElement} */ (slide)));
        }
      },
      { threshold: 0 }
    );

    this.#videoObserver.observe(this);
  }

  #pauseVideosNotInCarouselView() {
    if (!this.#embla) return;

    const slidesInView = new Set(this.#embla.slidesInView());
    const slideNodes = this.#embla.slideNodes();

    this.querySelectorAll('[data-item-video]').forEach((videoSlide) => {
      const slide = videoSlide.closest('[data-slide]');
      if (!slide) return;

      const index = slideNodes.indexOf(slide);
      if (index === -1 || !slidesInView.has(index)) {
        this.#pauseVideo(/** @type {HTMLElement} */ (videoSlide));
      }
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #initEmbla(signal) {
    const viewport = this.querySelector('[data-media-viewport]');
    const slides = this.querySelectorAll('[data-slide]');
    if (!viewport || slides.length <= 1) return;

    this.#embla = EmblaCarousel(
      viewport,
      {
        align: 'start',
        dragFree: true,
        containScroll: 'trim',
        slidesToScroll: 'auto',
      },
      [Accessibility(), WheelGesturesPlugin()]
    );

    addAccessibility(this.#embla);
    addDots(this.#embla);

    const prevBtn = this.querySelector('[data-embla-button="prev"]');
    const nextBtn = this.querySelector('[data-embla-button="next"]');
    if (prevBtn && nextBtn)
      addPrevNextButtonClickHandlers(this.#embla, prevBtn, nextBtn, { signal });

    const pauseVideosNotInCarouselView = () => this.#pauseVideosNotInCarouselView();

    this.#embla.on('scroll', pauseVideosNotInCarouselView);
    this.#embla.on('select', pauseVideosNotInCarouselView);
    this.#embla.on('reinit', pauseVideosNotInCarouselView);
  }

  /**
   * Load and bind Fancybox on the first gallery click, then open that slide.
   *
   * @param {AbortSignal} signal
   */
  #initFancybox(signal) {
    if (!this.querySelector(this.#fancyboxSelector)) return;

    this.addEventListener('click', this.#handleFancyboxClick, { signal });
  }

  #handleFancyboxClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const trigger = target.closest(this.#fancyboxSelector);
    if (!(trigger instanceof HTMLElement)) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

    event.preventDefault();
    if (this.#fancyboxLoading) return;

    trigger.ariaBusy = 'true';
    this.#fancyboxLoading = this.#openFancybox(trigger);
  };

  /**
   * @param {HTMLElement} trigger
   */
  async #openFancybox(trigger) {
    try {
      const { Fancybox } = await import('./index-QipeLQ5W.js');
      if (!this.isConnected || !trigger.isConnected) return;

      // A cached import resolves as a microtask, before paint. Yield so the
      // click can paint the busy state before Fancybox builds the gallery.
      await new Promise((resolve) => {
        setTimeout(resolve, 0);
      });
      if (!this.isConnected || !trigger.isConnected) return;

      const selector = this.#fancyboxSelector;
      this.#unbindFancybox?.();
      Fancybox.bind(this, selector, this.#fancyboxOptions());
      this.#unbindFancybox = () => {
        Fancybox.unbind(this, selector);
        const instance = Fancybox.getInstance();
        const triggerEl = instance?.getOptions().triggerEl;
        if (!triggerEl || !this.contains(triggerEl)) return;

        instance.destroy();
      };

      this.removeEventListener('click', this.#handleFancyboxClick);
      Fancybox.fromTriggerEl(trigger);
    } catch {
      // Allow another click to retry if Fancybox failed to load.
    } finally {
      trigger.ariaBusy = null;
      this.#fancyboxLoading = undefined;
    }
  }

  #fancyboxOptions() {
    const buttonClass =
      'button button--secondary icon-only w-11.5 h-11.5 rounded-sm border border-primary box-border';
    const prevArrowClass = `${buttonClass} absolute left-3 lg:left-10`;
    const nextArrowClass = `${buttonClass} absolute right-3 lg:right-10`;
    const prevIcon = `<div class="icon flex justify-center" data-icon="regular-chevron-left"><svg viewBox="0 0 24 24" class="size-6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.4839 15.786C13.1904 16.5979 13.8161 17.411 14.4243 18.3051L13.4268 18.9714C11.6773 16.4515 9.55071 14.288 7 12.4918C9.56494 10.6934 11.6669 8.56025 13.4244 6L14.4262 6.63195L13.4439 8.02124L12.2907 9.42841C11.0199 10.9792 9.03671 12.4857 9.03671 12.4857C9.03671 12.4857 11.0199 14.0112 12.4839 15.786Z" fill="currentColor" stroke="none"></path></svg></div>`;
    const nextIcon = `<div class="icon flex justify-center" data-icon="regular-chevron-right"><svg viewBox="0 0 24 24" class="size-6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5161 9.21403C9.8096 8.40213 9.1839 7.58903 8.5757 6.69493L9.5732 6.02863C11.3227 8.54853 13.4493 10.712 16 12.5082C13.4351 14.3066 11.3331 16.4397 9.5756 19L8.5738 18.3681L9.5561 16.9788L10.7093 15.5716C11.9801 14.0208 13.9633 12.5143 13.9633 12.5143C13.9633 12.5143 11.9801 10.9888 10.5161 9.21403Z" fill="currentColor" stroke="none"></path></svg></div>`;
    const closeIcon = `<div class="icon flex justify-center" data-icon="regular-close"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 18L18 6M6 6L18 18" stroke="currentColor" fill="none" stroke-linejoin="round"/></svg></div>`;

    return {
      groupAll: true,
      Carousel: {
        Panzoom: {
          touch: (panzoom, event) => event?.target.nodeName !== 'MODEL-VIEWER',
        },
        Thumbs: {
          type: 'classic',
        },
        Arrows: {
          prevClass: prevArrowClass,
          nextClass: nextArrowClass,
          prevTpl: prevIcon,
          nextTpl: nextIcon,
        },
        Toolbar: {
          display: {
            class: 'px-site',
            left: [],
            middle: [],
            right: ['close'],
          },
          items: {
            close: {
              tpl: `<button class="f-button ${buttonClass}" title="{{CLOSE}}" data-fancybox-close>${closeIcon}</button>`,
            },
          },
        },
      },
    };
  }
}

customElements.define('product-media', ProductMedia);
